import React, { FC, useState } from 'react';
import { getShortLanguageCode } from 'utils/format';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getPtjClinicsTemplate, organizationLookupTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ClinicFormData } from 'models/forms';
import ClinicForm from 'components/shared/clinic-form/clinic-form.component';
import {
    addWhiteLabelClinicTemplate,
    connectPtjClinicToLabTemplate,
    connectWhiteLabelClinicToLabTemplate,
    toggleCustomerEnabledTemplate,
} from 'apollo/templates/client-directory';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { excludeObjectKeys } from 'utils/exclude-object-key';
import OrganizationLookup, {
    OrganizationLookupFormData,
} from 'components/shared/organization-lookup/organization-lookup.component';
import { countryFromCode, getOrganizationDefaultCountryCode } from 'utils/country-helper';
import { Country } from 'models/country';
import CustomerList from './customer-list/customer-list.component';
import { OrderDirection } from 'models/search-result';
import { geocodeAddress } from 'utils/google-places';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Clinic } from 'models/clinic';
import { connectLabCustomerTemplate } from 'apollo/templates/client-directory/mutation.graphql';

type AddWhiteLabelClinicDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onCustomerAdded: () => void;
};
const AddWhiteLabelClinicDialog: FC<AddWhiteLabelClinicDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    onCustomerAdded,
}: AddWhiteLabelClinicDialogProps) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [activeStep, setActiveStep] = useState('lookup');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [customer, setCustomer] = useState<Partial<Organization>>();
    const [customers, setCustomers] = useState<
        (Partial<Organization> & { isAlreadyConnected: boolean; connectionIsActive?: boolean | null })[]
    >([]);
    const currentLab = currentOrgVar();
    const defaultCountryCode = getOrganizationDefaultCountryCode(currentLab);
    const [country] = useState<Country | undefined>(countryFromCode(defaultCountryCode));
    const user = currentUserVar();

    const [organizationLookup] = useLazyQuery(organizationLookupTemplate, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            const [firstError] = error.graphQLErrors;
            // const errorType = (firstError.extensions?.exception as any)?.otherInfo?.errorType;
            setIsLoading(false);
            if (firstError.message.includes('HitHorizons: No results found')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_NOT_FOUND'));
                return;
            }
            if (firstError.message.includes('organization with same hitHorizonsId already exists')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_ALREADY_EXISTS'));
                return;
            }
            showBoundary(firstError);
        },
        onCompleted: async ({ organizationLookup }) => {
            const organizations: (Partial<Organization> & {
                isAlreadyConnected: boolean;
                connectionIsActive?: boolean | null;
            })[] = [];
            for (const org of organizationLookup || []) {
                organizations.push({
                    _id: org.organizationDbId,
                    hitHorizonsId: org.hitHorizonsId,
                    name: org.name,
                    type: org.type,
                    isAlreadyConnected: org.isAlreadyConnected,
                    connectionIsActive: org.connectionIsActive,
                    officialName: org.name,
                    organizationNumber: org.organizationNumber,
                    contact: {
                        communication: { site: org.site || undefined },
                        address: await geocodeAddress(org.address, getShortLanguageCode(user.localeFormat?.language)),
                    },
                    invoicing: {
                        vatNo: org.vatNumber,
                        costCenter: org.costCenter,
                    },
                });
            }
            setIsLoading(false);
            if (organizations.length === 1 && !organizations[0].type) {
                setCustomer(organizations[0]);
                setActiveStep('add_clinic');
                return;
            }
            setCustomers(organizations);
            setActiveStep('customer_list');
        },
    });

    const [addWhiteLabelClinic] = useMutation(addWhiteLabelClinicTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            onCustomerAdded();
            handleCloseDialog();
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const [connectWhiteLabelClinicToLab] = useMutation(connectWhiteLabelClinicToLabTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            setIsLoading(false);
            onCustomerAdded();
            handleCloseDialog();
        },
        onError(error) {
            setIsLoading(false);
            showBoundary(error);
        },
    });

    const [connectLabCustomer] = useMutation(connectLabCustomerTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            setIsLoading(false);
            onCustomerAdded();
            handleCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [toggleCustomerEnabled] = useMutation(toggleCustomerEnabledTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            setIsLoading(false);
            onCustomerAdded();
            handleCloseDialog();
        },
        onError(error) {
            setIsLoading(false);
            showBoundary(error);
        },
    });

    const [connectPtjClinicToLab] = useMutation(connectPtjClinicToLabTemplate, {
        onCompleted: () => {
            setIsLoading(false);
            onCustomerAdded();
            handleCloseDialog();
        },
        onError: (error) => {
            setIsLoading(false);
            const [firstError] = error.graphQLErrors;
            const errorType = (firstError.extensions?.exception as any)?.otherInfo?.errorType;
            if (errorType === 'LAB_ALREADY_CONNECTED') {
                setErrorMessage(translate('client-directory:PTJ_ALREADY_CONNECTED'));
                return;
            }
            showBoundary(error);
        },
    });

    const handleOrganizationSelected = (selectOrganizationForm: OrganizationLookupFormData) => {
        setIsLoading(true);
        organizationLookup({
            variables: {
                organizationNumber: selectOrganizationForm.organizationNumber,
                countryCode: selectOrganizationForm.countryCode,
                checkWhitelabel: true,
            },
        });
    };

    const handleAddPtjClicked = () => {
        setIsLoading(true);
        getPtjClinics();
    };

    const [getPtjClinics] = useLazyQuery(getPtjClinicsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: {},
            limit: 0,
            offset: 0,
            orderBy: 'name',
            orderDirection: OrderDirection.asc,
        },
        onCompleted: ({ ptjClinics }) => {
            const mappedClinics = ptjClinics?.map((clinic: any) => ({
                _id: clinic.organizationDbId,
                hitHorizonsId: clinic.hitHorizonsId,
                name: clinic.name,
                type: clinic.type,
                isAlreadyConnected: clinic.isAlreadyConnected,
                officialName: clinic.name,
                organizationNumber: clinic.organizationNumber,
                contact: {
                    communication: { site: clinic.site || undefined },
                    address: clinic.address,
                },
                invoicing: {
                    vatNo: clinic.vatNumber,
                    costCenter: clinic.costCenter,
                },
            }));
            setCustomers(mappedClinics);
            setActiveStep('ptj_clinic_list');
            setIsLoading(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleCloseDialog = () => {
        onCloseDialog();
        resetForm();
    };

    const resetForm = () => {
        setActiveStep('lookup');
        setErrorMessage(undefined);
        setIsLoading(false);
    };

    const handleFormSubmitted = (formData: ClinicFormData) => {
        setIsLoading(true);
        addWhiteLabelClinic({
            variables: {
                labId: currentLab._id,
                clinic: excludeObjectKeys(formData, [
                    '_id' as unknown as keyof ClinicFormData,
                    'isAlreadyConnected' as unknown as keyof ClinicFormData,
                    'type' as unknown as keyof ClinicFormData,
                ]),
            },
        });
    };

    const handleManualAddClicked = () => {
        setActiveStep('add_clinic');
    };

    const handleSelectPtjClinic = (clinicId: string) => {
        setIsLoading(true);
        connectPtjClinicToLab({
            variables: {
                clinicId: clinicId,
                labId: currentLab._id,
            },
        });
    };

    const handleAddCustomer = (customerId: string, customerType?: string) => {
        setIsLoading(true);
        if (customerType === 'lab') {
            return connectLabCustomer({
                variables: {
                    customerId: customerId,
                },
            });
        }
        connectWhiteLabelClinicToLab({
            variables: {
                clinicId: customerId,
                labId: currentLab._id,
            },
        });
    };

    const handleActivateCustomer = (customerId: string) => {
        setIsLoading(true);
        toggleCustomerEnabled({
            variables: {
                customerId: customerId,
                isEnabled: true,
            },
        });
    };

    const getStepContent = (step: string) => {
        switch (step) {
            case 'lookup':
                return (
                    <OrganizationLookup
                        onOrganizationSelected={handleOrganizationSelected}
                        onClose={handleCloseDialog}
                        country={country}
                        onOrganizationChanged={() => setErrorMessage(undefined)}
                        onManualAddClicked={handleManualAddClicked}
                        showAddPtj
                        onAddPtjClicked={handleAddPtjClicked}
                    />
                );
            case 'add_clinic':
                return (
                    <ClinicForm
                        clinic={(customer as Clinic) || {}}
                        onClose={handleCloseDialog}
                        onFormSubmitted={handleFormSubmitted}
                        submitButtonText={translate('organization:ADD_CUSTOMER')}
                        showDentistReminder
                        hideExtendedFields
                        defaultCountryCode={defaultCountryCode}
                    />
                );
            case 'ptj_clinic_list':
                return (
                    <CustomerList
                        customers={customers}
                        onSelectCustomer={handleSelectPtjClinic}
                        addButtonTitle={translate('organization:ADD_CUSTOMER')}
                    />
                );
            case 'customer_list':
                return (
                    <CustomerList
                        showTypeFilter={true}
                        customers={customers}
                        onSelectCustomer={handleAddCustomer}
                        onActivateCustomer={handleActivateCustomer}
                        addButtonTitle={translate('organization:ADD_CUSTOMER')}
                    />
                );
            default:
                break;
        }
    };

    return (
        <Dialog fullWidth maxWidth="md" open={dialogOpen} onClose={handleCloseDialog}>
            <DialogTitle>{translate('organization:ADD_CUSTOMER')}</DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
                {isLoading ? (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                ) : (
                    getStepContent(activeStep)
                )}
            </DialogContent>
            {activeStep === 'ptj_clinic_list' || activeStep === 'customer_list' ? (
                <DialogActions sx={{ justifyContent: 'flex-start' }}>
                    <Button onClick={handleCloseDialog}>{translate('common:CANCEL')}</Button>
                </DialogActions>
            ) : undefined}
        </Dialog>
    );
};

export default AddWhiteLabelClinicDialog;
